import React from "react";
import { Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import {
  ArrowRight, ShieldCheck, CalendarCheck, Users, Target, BarChart3,
  Building2, Sparkles, Check, LayoutGrid,
} from "lucide-react";
import { MarketingHeader, MarketingFooter } from "@/components/MarketingLayout";
import { Button } from "@/components/ui/button";

const FEATURE_ICONS = [Users, Target, CalendarCheck, BarChart3];
const CHIP_ICONS = [ShieldCheck, Building2, CalendarCheck, BarChart3];

export default function Landing() {
  const { t } = useTranslation();
  const features = t("landing.features", { returnObjects: true }) || [];
  const steps = t("landing.steps", { returnObjects: true }) || [];
  const heroBadges = t("landing.heroBadges", { returnObjects: true }) || [];
  const expertsChips = t("landing.expertsChips", { returnObjects: true }) || [];

  return (
    <div className="min-h-screen bg-background">
      <MarketingHeader />

      {/* Hero */}
      <section id="product" className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-[520px] glow pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-20 pb-16 md:pt-28 md:pb-24 grid lg:grid-cols-[1.22fr_0.78fr] gap-x-14 gap-y-6 items-start">
          <span className="lg:col-span-2 w-fit justify-self-start inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground shadow-sm animate-fade-up">
            <Sparkles size={13} className="text-primary" /> {t("landing.heroBadge")}
          </span>
          <div className="animate-fade-up">
            <h1 className="text-4xl sm:text-5xl lg:text-[3.15rem] xl:text-[3.4rem] font-semibold tracking-tight leading-[1.4] [text-wrap:balance] text-foreground">
              <Trans i18nKey="landing.heroTitle" components={{ hl: <span className="text-primary" /> }} />
            </h1>
            <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-lg">
              {t("landing.heroSubtitle")}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/register/company">
                <Button data-testid="hero-register" size="lg" className="rounded-lg h-12 px-6 text-[15px] active:scale-[0.98] transition-transform">
                  {t("landing.heroCtaCompany")} <ArrowRight size={17} className="ml-1.5" />
                </Button>
              </Link>
              <Link to="/register/expert">
                <Button data-testid="hero-expert" size="lg" variant="outline" className="rounded-lg h-12 px-6 text-[15px] bg-card">
                  {t("landing.heroCtaExpert")}
                </Button>
              </Link>
            </div>
          </div>

          {/* Hero image */}
          <div className="relative animate-fade-up d2 min-h-[360px] lg:min-h-0 lg:self-stretch">
            <div className="card-milled rounded-2xl p-2 shadow-float h-full">
              <div className="relative h-full w-full overflow-hidden rounded-xl">
                <img
                  src="https://images.unsplash.com/photo-1573497620053-ea5300f94f21?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000&h=1150&fit=crop"
                  alt={t("landing.heroImageAlt")}
                  loading="eager"
                  fetchpriority="high"
                  className="absolute inset-0 h-full w-full object-cover"
                  data-testid="hero-image"
                />
              </div>
            </div>
            <div className="absolute bottom-5 left-5 right-5 flex items-center gap-2.5 rounded-xl border border-border bg-card/95 backdrop-blur p-3 shadow-float">
              <span className="w-8 h-8 rounded-full bg-primary/10 grid place-items-center text-primary text-xs font-semibold">EM</span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium">Elena Marconi</p>
                <p className="text-[10px] text-muted-foreground">{t("landing.previewConfirmedMeta")}</p>
              </div>
              <span className="text-[10px] font-medium text-success bg-success/10 rounded-full px-2 py-0.5">{t("landing.previewConfirmed")}</span>
            </div>
          </div>
          <div className="lg:col-span-2 animate-fade-up d2">
            <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-sm text-muted-foreground">
              {heroBadges.map((tx) => (
                <span key={tx} className="inline-flex items-center gap-1.5"><Check size={15} className="text-success" strokeWidth={2.5} /> {tx}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="organisations" className="max-w-6xl mx-auto px-5 sm:px-8 py-16 md:py-24">
        <div className="max-w-2xl animate-fade-up">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-primary">
            <LayoutGrid size={13} /> {t("landing.featuresEyebrow")}
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-[2.7rem] font-semibold tracking-tight leading-[1.35] [text-wrap:balance] text-foreground">
            <Trans i18nKey="landing.featuresTitle" components={{ hl: <span className="text-primary" /> }} />
          </h2>
          <div className="mt-5 h-[3px] w-14 rounded-full bg-gradient-to-r from-primary to-primary/20" />
          <p className="mt-5 text-muted-foreground text-base leading-relaxed">{t("landing.featuresSubtitle")}</p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f, i) => {
            const Icon = FEATURE_ICONS[i] || Users;
            return (
              <div key={f.title} className={`card-milled rounded-[0.8rem] p-6 animate-fade-up d${i + 1} transition-transform duration-200 ease-out hover:-translate-y-0.5 hover:shadow-soft`}>
                <span className="grid place-items-center w-10 h-10 rounded-lg bg-primary/10 text-primary mb-4">
                  <Icon size={19} strokeWidth={1.75} />
                </span>
                <h3 className="text-[15px] font-medium">{f.title}</h3>
                <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{f.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-sidebar border-y border-border">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 md:py-24">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-primary">
            <Building2 size={13} /> {t("landing.stepsEyebrow")}
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-[2.7rem] font-semibold tracking-tight leading-[1.35] [text-wrap:balance] max-w-2xl text-foreground">
            <Trans i18nKey="landing.stepsTitle" components={{ hl: <span className="text-primary" /> }} />
          </h2>
          <div className="mt-5 h-[3px] w-14 rounded-full bg-gradient-to-r from-primary to-primary/20" />
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <div key={s.title} className="relative">
                <span className="text-sm font-mono font-semibold text-primary">{String(i + 1).padStart(2, "0")}</span>
                <div className="mt-3 h-px w-full bg-border" />
                <h3 className="mt-4 text-lg font-medium">{s.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Experts CTA */}
      <section id="experts" className="max-w-6xl mx-auto px-5 sm:px-8 py-16 md:py-24">
        <div className="card-milled rounded-2xl p-8 md:p-12 grid lg:grid-cols-[1.2fr_0.8fr] gap-8 items-center relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-72 h-72 glow pointer-events-none" />
          <div className="relative">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-primary">
              <ShieldCheck size={13} /> {t("landing.expertsEyebrow")}
            </span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-[2.6rem] font-semibold tracking-tight leading-[1.35] [text-wrap:balance] text-foreground">
              <Trans i18nKey="landing.expertsTitle" components={{ hl: <span className="text-primary" /> }} />
            </h2>
            <div className="mt-5 h-[3px] w-14 rounded-full bg-gradient-to-r from-primary to-primary/20" />
            <p className="mt-3 text-muted-foreground leading-relaxed max-w-lg">
              {t("landing.expertsBody")}
            </p>
            <Link to="/register/expert">
              <Button data-testid="cta-expert" size="lg" className="mt-7 rounded-lg h-12 px-6 active:scale-[0.98] transition-transform">
                {t("landing.expertsCta")} <ArrowRight size={17} className="ml-1.5" />
              </Button>
            </Link>
          </div>
          <div className="relative grid grid-cols-2 gap-3">
            {expertsChips.map((tx, i) => {
              const Icon = CHIP_ICONS[i] || Check;
              return (
                <div key={tx} className="card-milled rounded-xl p-4 flex items-center gap-2.5 transition-transform duration-200 ease-out hover:-translate-y-0.5 hover:shadow-soft">
                  <Icon size={17} className="text-primary" strokeWidth={1.75} />
                  <span className="text-sm font-medium">{tx}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
